/**
 * sync.ts — Busca de chats e mensagens via Uazapi API.
 * 
 * BUGFIX CRÍTICO: A Uazapi ignora o filtro 'where' no endpoint message/find 
 * e retorna TODAS as mensagens da instância misturadas. O código aplica filtro 
 * local obrigatório por chatid e faz paginação com offset para buscar o histórico completo.
 */
import { fetchAPI } from './client';
import { createModuleLogger } from '../../utils/logger';
import { jidsMatch } from '../../utils/phoneUtils';
import {
    UazapiChat,
    UazapiChatListResponse,
    UazapiMessage,
    UazapiMessageFindResponse,
    FetchMessagesOptions
} from './types';

// Re-exporta tipos e utils para compatibilidade com imports existentes
export * from './types';
export { isFromMe, mapMessageType, normalizePhoneToJid } from './messageUtils';

const log = createModuleLogger('[UazapiSync]');

/**
 * Busca lista de chats de uma instância.
 * A Uazapi pode retornar array direto ou objeto com .chats.
 */
export async function fetchChatList(
    instanceName: string,
    page = 1,
    limit = 50,
    owner?: string
): Promise<UazapiChatListResponse> {
    const offset = (page - 1) * limit;
    try {
        const payload: { offset: number; limit: number; owner?: string } = { offset, limit };
        if (owner) payload.owner = owner;

        const data = await fetchAPI<UazapiChatListResponse>('chat/find', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'instance': instanceName },
            body: JSON.stringify(payload)
        });
        
        const chats = extractChatsFromResponse(data);
        log.info(`Chats extraídos: ${chats.length}`);

        return {
            chats,
            pagination: (data as any)?.pagination || {
                currentPage: page,
                hasNextPage: false,
                totalPages: 0,
                totalRecords: 0,
                pageSize: limit
            },
            totalChatsStats: (data as any)?.totalChatsStats,
            _debug: (data as any)?._debug
        };
    } catch (error) {
        log.error('Erro ao buscar lista de chats:', error);
        return {
            chats: [],
            pagination: { currentPage: page, hasNextPage: false, totalPages: 0, totalRecords: 0, pageSize: limit }
        };
    }
}

/**
 * Busca TODOS os chats paginando automaticamente.
 * Filtra grupos (exclui @g.us e @lid).
 */
export async function fetchAllChats(
    instanceName: string,
    maxPages = 20,
    pageSize = 50,
    ownerJid?: string
): Promise<UazapiChat[]> {
    const allChats: UazapiChat[] = [];
    let page = 1;

    while (page <= maxPages) {
        const response = await fetchChatList(instanceName, page, pageSize, ownerJid);
        const individualChats = response.chats.filter(c => isIndividualChat(c));
        allChats.push(...individualChats);
        if (!response.pagination.hasNextPage) break;
        page++;
    }

    log.info(`Total de chats individuais: ${allChats.length}`);
    return allChats;
}

/**
 * Busca mensagens de um chat específico COM PAGINAÇÃO COMPLETA.
 * 
 * BUGFIX CRÍTICO: A Uazapi ignora o filtro 'where' e retorna TODAS as mensagens 
 * da instância misturadas. Este método:
 * 1) Faz paginação com offset/limit para buscar todo o histórico global
 * 2) Aplica filtro local rigoroso por chatid para separar corretamente
 * 3) Retorna mensagens do chat (mídias via webhook/R2 nas mensagens novas)
 */
export async function fetchMessages(
    instanceName: string,
    remoteJid: string,
    options?: FetchMessagesOptions
): Promise<UazapiMessage[]> {
    const targetLimit = options?.limit || 500;
    const jid = remoteJid.includes('@') ? remoteJid : `${remoteJid}@s.whatsapp.net`;
    
    try {
        log.info(`[UazapiSync] Buscando mensagens para ${jid} via message/find (paginado)...`);
        
        // Buscar TODAS as mensagens da instância com paginação
        const allRawMessages: any[] = [];
        let offset = 0;
        const pageSize = 500; // Máximo que a API suporta por request
        let hasMore = true;
        let pageCount = 0;
        const maxPages = 4; // Evita saturar UAZAPI em multitenant

        while (hasMore && pageCount < maxPages) {
            pageCount++;
            const data = await fetchAPI<any>('message/find', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'instance': instanceName },
                body: JSON.stringify({ limit: pageSize, offset })
            });

            // DEBUG: Log do formato recebido
            const dKeys = data ? Object.keys(data) : [];
            console.log(`[fetchMessages] 📦 P${pageCount}: isArr=${Array.isArray(data)}, keys=[${dKeys.join(',')}], msgs=${data?.messages?.length ?? 'X'}, chats=${data?.chats?.length ?? 'X'}`);

            // O proxy embala arrays em { chats: [...] }, verificar todos os formatos
            let pageMessages: any[] = [];
            if (data?.messages && Array.isArray(data.messages)) pageMessages = data.messages;
            else if (data?.chats && Array.isArray(data.chats)) pageMessages = data.chats;
            else if (data?.data?.messages && Array.isArray(data.data.messages)) pageMessages = data.data.messages;
            else if (data?.data && Array.isArray(data.data)) pageMessages = data.data;
            else if (Array.isArray(data)) pageMessages = data;

            allRawMessages.push(...pageMessages);

            // Para se retornou menos msgs que o pageSize (fim dos dados)
            hasMore = pageMessages.length >= pageSize;
            offset += pageSize;
        }

        log.info(`[UazapiSync] Total bruto recebido: ${allRawMessages.length} msgs em ${pageCount} páginas`);

        // ====== FILTRO LOCAL CRÍTICO ======
        // A Uazapi ignora filtros 'where' e retorna o histórico global da instância.
        // Filtramos localmente apenas as mensagens do JID alvo.
        const filtered = allRawMessages.filter(m => {
            const mChatId = m.chatid || m.key?.remoteJid;
            return mChatId && jidsMatch(mChatId, jid);
        });

        log.info(`[UazapiSync] Filtrado para ${jid}: ${filtered.length} msgs (de ${allRawMessages.length} globais)`);

        // Limita ao target solicitado
        const limited = filtered.slice(0, targetLimit);

        const mapped = limited.map(mapRawBaileysToUazapi).filter(Boolean) as UazapiMessage[];
        
        log.info(`[UazapiSync] 📨 ${mapped.length} mensagens válidas parseadas para ${jid}`);
        return mapped;
    } catch (error) {
        log.error(`[UazapiSync] Erro ao buscar mensagens para ${jid}:`, error);
        return [];
    }
}

// --- Helpers privados ---


/** Mapear mensagem bruta do Baileys para o formato UazapiMessage interno do Frontend */
function mapRawBaileysToUazapi(msg: any): UazapiMessage | null {
    if (!msg) return null;
    // Se já estiver no formato correto (messageid definido no top-level), retorna como está
    if (msg.messageid) return msg as UazapiMessage;

    // Converte formato originário do Baileys
    const key = msg.key || {};
    const message = msg.message || {};
    
    const messageType = Object.keys(message).find(k => k !== 'messageContextInfo') || 'conversation';
    const contentObj = message[messageType] || message;
    
    let text = typeof contentObj === 'string' ? contentObj : (contentObj.text || contentObj.caption || '');
    if (messageType === 'conversation') text = typeof message.conversation === 'string' ? message.conversation : text;
    
    // Tratamento para ExtendedTextMessage
    if (messageType === 'extendedTextMessage') {
        text = message.extendedTextMessage?.text || text;
    }

    return {
        messageid: key.id || msg.id,
        chatid: key.remoteJid,
        text: text,
        sender: msg.participant || key.remoteJid,
        fromMe: key.fromMe || false,
        messageType: messageType.charAt(0).toUpperCase() + messageType.slice(1) + (messageType.endsWith('Message') ? '' : 'Message'),
        messageTimestamp: msg.messageTimestamp || msg.timestamp || Math.floor(Date.now() / 1000),
        fileURL: msg.fileURL || null,
        owner: '', 
        quoted: contentObj?.contextInfo?.stanzaId || null,
        key // preserva a key caso o isFromMe fallback precise
    } as UazapiMessage;
}

/** Extrai array de chats da resposta (diversos formatos possíveis) */
function extractChatsFromResponse(data: any): UazapiChat[] {
    if (Array.isArray(data)) return data;
    if (data?.chats && Array.isArray(data.chats)) return data.chats;
    if (typeof data === 'object' && data !== null) {
        const keys = Object.keys(data).filter(k => k !== '_debug');
        for (const k of keys) {
            if (Array.isArray(data[k])) return data[k];
        }
    }
    return [];
}

/** Verifica se é chat individual (não grupo) */
function isIndividualChat(chat: UazapiChat): boolean {
    const phone = chat.phone || '';
    const chatid = (chat as any).wa_chatid || '';
    const isGroup = (chat as any).wa_isGroup === true;
    return !isGroup && 
           !phone.includes('@g.us') && !phone.includes('@lid') &&
           !chatid.includes('@g.us') && !chatid.includes('@lid');
}
